import React, { useState, useEffect, useCallback, useMemo } from 'react';

/**
 * Consent management — DSGVO / § 25 TDDDG / Google Consent Mode v2.
 *
 * - Defaults are set in public/index.html BEFORE GTM loads (all denied for EEA/CH/GB).
 * - This module renders a bottom-banner on first visit, persists the user's
 *   choice in localStorage, and calls gtag('consent','update', …) so GTM tags
 *   (GA4, Google Ads, Meta Pixel) only fire when allowed.
 * - "Cookie-Einstellungen" link in the footer re-opens the settings modal.
 *
 * Public API:
 *   <ConsentBanner />          — mount once at the root
 *   openConsentSettings()      — call from anywhere (e.g. footer link) to reopen
 *   getConsent() / onChange    — for code that wants to read/listen
 */

const STORAGE_KEY = '__hamba_consent_v1';
const CONSENT_VERSION = 1;
const OPEN_SETTINGS_EVENT = 'hamba:open-consent-settings';
const CHANGE_EVENT = 'hamba:consent-change';

const CATEGORIES = {
  necessary: { label: 'Notwendig', locked: true, defaultGranted: true,  hint: 'Session, Authentifizierung, Speicherung Ihrer Cookie-Auswahl.' },
  analytics: { label: 'Statistik',  locked: false, defaultGranted: false, hint: 'Google Analytics 4 zur anonymisierten Nutzungsanalyse.' },
  marketing: { label: 'Marketing',  locked: false, defaultGranted: false, hint: 'Google Ads Conversion-Tracking und Meta-Pixel (Facebook/Instagram).' },
};

function defaultState() {
  return Object.fromEntries(
    Object.entries(CATEGORIES).map(([k, v]) => [k, v.defaultGranted])
  );
}

function loadConsent() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed.version !== CONSENT_VERSION) return null;
    return { state: parsed.state, timestamp: parsed.timestamp };
  } catch { return null; }
}

function saveConsent(state) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify({
      version: CONSENT_VERSION,
      timestamp: Date.now(),
      state,
    }));
  } catch { /* private mode / quota — ignore */ }
}

// Translate our categories → Google Consent Mode v2 signals
function applyToGoogle(state) {
  // Mark that consent has been settled this page-load. metaTrack reads this to
  // know GTM's Consent Mode state is updated, so it can flush queued Meta events
  // AFTER the gtag('consent','update') below — otherwise the browser Pixel tag
  // fires while consent is still 'denied' and gets blocked (the missed first event).
  if (typeof window !== 'undefined') window.__hambaConsentApplied = true;
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
  const signals = {
    ad_storage: state.marketing ? 'granted' : 'denied',
    ad_user_data: state.marketing ? 'granted' : 'denied',
    ad_personalization: state.marketing ? 'granted' : 'denied',
    analytics_storage: state.analytics ? 'granted' : 'denied',
  };
  window.gtag('consent', 'update', signals);
  // Helpful breadcrumb for GTM custom tags that prefer dataLayer events
  if (Array.isArray(window.dataLayer)) {
    window.dataLayer.push({ event: 'hamba_consent_update', consent: state });
  }
  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.log('[consent] gtag update + dataLayer push', { signals, state });
  }
}

function dispatchChange(state) {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: state }));
}

/** Public helper: read the current consent state (or null if undecided). */
export function getConsent() {
  const loaded = loadConsent();
  return loaded ? loaded.state : null;
}

/** Public helper: trigger the settings modal from anywhere (e.g. footer link). */
export function openConsentSettings() {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new Event(OPEN_SETTINGS_EVENT));
}

/** React hook: subscribe to consent changes. */
export function useConsent() {
  const [state, setState] = useState(() => getConsent());
  useEffect(() => {
    const onChange = (e) => setState(e.detail);
    window.addEventListener(CHANGE_EVENT, onChange);
    return () => window.removeEventListener(CHANGE_EVENT, onChange);
  }, []);
  return state;
}

// ─────────────────────────────────────────────────────────────────────────
//   Banner component
// ─────────────────────────────────────────────────────────────────────────

export function ConsentBanner() {
  // null = undecided (show banner); object = saved (hide banner)
  const [saved, setSaved] = useState(undefined); // undefined = loading from storage
  const [showSettings, setShowSettings] = useState(false);
  const [draft, setDraft] = useState(defaultState());

  // On mount: load stored state and apply
  useEffect(() => {
    const loaded = loadConsent();
    if (loaded) {
      setSaved(loaded.state);
      applyToGoogle(loaded.state);
      dispatchChange(loaded.state);
    } else {
      setSaved(null);
    }
  }, []);

  // Listen for "open settings" events
  useEffect(() => {
    const handler = () => {
      const current = loadConsent()?.state || defaultState();
      setDraft(current);
      setShowSettings(true);
    };
    window.addEventListener(OPEN_SETTINGS_EVENT, handler);
    return () => window.removeEventListener(OPEN_SETTINGS_EVENT, handler);
  }, []);

  const persistAndApply = useCallback((next) => {
    saveConsent(next);
    applyToGoogle(next);
    dispatchChange(next);
    setSaved(next);
    setShowSettings(false);
  }, []);

  const acceptAll = useCallback(() => {
    persistAndApply(Object.fromEntries(Object.keys(CATEGORIES).map((k) => [k, true])));
  }, [persistAndApply]);

  const rejectAll = useCallback(() => {
    persistAndApply(Object.fromEntries(
      Object.entries(CATEGORIES).map(([k, v]) => [k, v.locked])
    ));
  }, [persistAndApply]);

  const saveDraft = useCallback(() => {
    // Ensure locked categories stay granted regardless of draft
    const final = { ...draft };
    Object.entries(CATEGORIES).forEach(([k, v]) => {
      if (v.locked) final[k] = true;
    });
    persistAndApply(final);
  }, [draft, persistAndApply]);

  const bannerVisible = saved === null && !showSettings;

  if (saved === undefined) return null; // still loading from storage

  return (
    <>
      {bannerVisible && (
        <div role="region" aria-label="Cookie-Hinweis" className="hamba-consent-banner">
          <div className="hamba-consent-banner__inner">
            <div className="hamba-consent-banner__text">
              <div className="hamba-consent-banner__title">Datenschutz</div>
              <p>
                Wir verwenden Cookies und ähnliche Technologien. Notwendige Cookies sind für den
                Betrieb der Website erforderlich. Für Statistik und Marketing benötigen wir Ihre
                Einwilligung. Sie können Ihre Auswahl jederzeit in den Einstellungen anpassen.
                Details in unserer <a href="/datenschutz" target="_blank" rel="noopener noreferrer">Datenschutzerklärung</a>.
              </p>
            </div>
            <div className="hamba-consent-banner__actions">
              <button type="button" className="hamba-consent-btn hamba-consent-btn--ghost" onClick={rejectAll}>
                Nur notwendige
              </button>
              <button
                type="button"
                className="hamba-consent-btn hamba-consent-btn--ghost"
                onClick={() => { setDraft(defaultState()); setShowSettings(true); }}
              >
                Einstellungen
              </button>
              <button type="button" className="hamba-consent-btn hamba-consent-btn--primary" onClick={acceptAll}>
                Alle akzeptieren
              </button>
            </div>
          </div>
        </div>
      )}

      {showSettings && (
        <ConsentSettingsModal
          draft={draft}
          setDraft={setDraft}
          onCancel={() => setShowSettings(false)}
          onSave={saveDraft}
          onAcceptAll={acceptAll}
          onRejectAll={rejectAll}
        />
      )}
    </>
  );
}

function ConsentSettingsModal({ draft, setDraft, onCancel, onSave, onAcceptAll, onRejectAll }) {
  const toggle = (key) => {
    if (CATEGORIES[key].locked) return;
    setDraft((d) => ({ ...d, [key]: !d[key] }));
  };

  const entries = useMemo(() => Object.entries(CATEGORIES), []);

  return (
    <div className="hamba-consent-modal" role="dialog" aria-modal="true" aria-label="Cookie-Einstellungen">
      <div className="hamba-consent-modal__backdrop" onClick={onCancel} />
      <div className="hamba-consent-modal__panel">
        <h2 className="hamba-consent-modal__title">Cookie-Einstellungen</h2>
        <p className="hamba-consent-modal__lede">
          Sie entscheiden, welche Kategorien Sie zulassen. Notwendige Cookies sind nicht
          abwählbar. Ihre Auswahl wird für 12 Monate gespeichert.
        </p>

        <div className="hamba-consent-modal__list">
          {entries.map(([key, meta]) => (
            <label key={key} className="hamba-consent-category">
              <div className="hamba-consent-category__text">
                <div className="hamba-consent-category__label">{meta.label}</div>
                <div className="hamba-consent-category__hint">{meta.hint}</div>
              </div>
              <input
                type="checkbox"
                className="hamba-consent-toggle"
                checked={!!draft[key]}
                disabled={meta.locked}
                onChange={() => toggle(key)}
                aria-label={meta.label}
              />
            </label>
          ))}
        </div>

        <div className="hamba-consent-modal__actions">
          <button type="button" className="hamba-consent-btn hamba-consent-btn--ghost" onClick={onRejectAll}>
            Nur notwendige
          </button>
          <button type="button" className="hamba-consent-btn hamba-consent-btn--ghost" onClick={onSave}>
            Auswahl speichern
          </button>
          <button type="button" className="hamba-consent-btn hamba-consent-btn--primary" onClick={onAcceptAll}>
            Alle akzeptieren
          </button>
        </div>
      </div>
    </div>
  );
}
